


















































































































































































































































import {
  computed,
  defineComponent,
  onBeforeMount,
  reactive,
  SetupContext,
  watch,
} from '@vue/composition-api';
import EntityDetails from '@/components/common/EntityDetails.vue';
import { Product } from '@/models/entities/Product';
import { CategoryPreview } from '@/models/entities/Category';
import ImageUpload from '@/components/utils/ImageUpload.vue';
import { DropdownFilter, IFieldFilter } from '@/models/filters/Filters';
import PaginatedAutocomplete from '@/components/utils/PaginatedAutocomplete.vue';
import ProductCategoryOptionsSimpleTable from '@/components/entities/productCategoryOptions&Attributes/ProductCategoryOptionsSimpleTable.vue';
import ProductCategoryAttributesSimpleBlock from '@/components/entities/productCategoryOptions&Attributes/ProductCategoryAttributesSimpleBlock.vue';
import SizeTable from '@/components/entities/products/productFields/SizesTable.vue';
import ColorizableAreasSimpleTable from '@/components/entities/products/productFields/ColorizableAreasSimpleTable.vue';
import { ColorizableArea, Location } from '@/models/entities/Location';
import LocationsSimpleTable from '@/components/entities/products/productFields/LocationsSimpleTable.vue';
import { ShopifyProduct } from '@/models/entities/ShopifyProduct';
import { getEntityStorePath } from '@/store/entityModules/utils';
import { EntityType } from '@/store/entityModules/types';
import LogoPlacementComponent from '@/components/entities/products/productFields/LogoPlacementComponent.vue';
import { ColorGroup } from '@/models/entities/ColorGroup';
import { FontGroup } from '@/models/entities/FontGroup';

interface Props {
  id: string;
}

interface State {
  entity: Product;
  loading: boolean;
  colorizableAreas: ColorizableArea[];
}

const Component = defineComponent({
  name: 'ProductDetails',

  components: {
    EntityDetails,
    ImageUpload,
    PaginatedAutocomplete,
    ProductCategoryOptionsSimpleTable,
    ProductCategoryAttributesSimpleBlock,
    SizeTable,
    ColorizableAreasSimpleTable,
    LocationsSimpleTable,
    LogoPlacementComponent,
  },

  props: {
    id: String,
  },

  setup(props: Props, { root: { $store, $router } }: SetupContext) {
    const product = new Product({});

    const colorGroups: ColorGroup[] = [];
    onBeforeMount(async () => {
      const res = await $store.dispatch(`productsModule/fetchItem`, props.id);

      if (!res) $router.back();
      else if (res.colorGroupIds) {
        const data = await $store.dispatch(
          `${getEntityStorePath(EntityType.COLOR_GROUP)}/fetchItems`,
        );
        const groups = data.filter((group: ColorGroup) =>
          res.colorGroupIds.includes(group.id),
        );

        Object.assign(colorGroups, groups);
      }
    });

    const state = reactive({
      entity: product,
      loading: computed(() => $store.getters['productsModule/loading']),
      colorizableAreas: computed(() =>
        product.locations
          .map(location => {
            const colorizableAreas = location.imageFile.colorizableAreas;
            if (!colorizableAreas) return [];
            colorizableAreas.forEach(
              area => (area.location = { name: location.name, id: location.id }),
            );
            return colorizableAreas;
          })
          .flat(),
      ),
    }) as State;

    const categoryFilterDropdown = new DropdownFilter({
      label: 'Category',
      itemsModuleName: 'categoriesModule',
      initialValue: product.categories.map(cat => cat.id),
    });
    const shopifyFilterDropdown = new DropdownFilter({
      label: 'Original Shopify Product',
      itemsModuleName: 'shopifyProductsModule',
      initialValue: product.shopifyProduct.id,
      textPropName: 'title',
    });
    const fontGroupFilterDropdown = new DropdownFilter({
      label: 'Font Group',
      itemsModuleName: 'fontGroupsModule',
      initialValue: product.fontGroup?.id,
    });

    const colorFilter: IFieldFilter = {
      filterBy: 'colorGroup',
      filterValue: product.colorGroupIds?.map(id => id.toString()) || [],
    };

    const unwatch = watch(
      () => $store.getters['productsModule/selectedItem'],
      (newValue: Product) => {
        Object.assign(product, newValue);
        categoryFilterDropdown.initialValue = product.categories.map(cat => cat.id);
        shopifyFilterDropdown.initialValue = product.shopifyProduct.id;
        fontGroupFilterDropdown.initialValue = product.fontGroup?.id;
        colorFilter.filterValue = product.colorGroupIds?.map(id => id.toString()) || [];
        if (product.id > 0 && unwatch) unwatch();
      },
      { immediate: false, deep: true },
    );

    const title = computed(() => {
      if (state.loading) return '';
      return `Edit Product ${$store.getters['productsModule/selectedItem']?.name}`;
    });

    const rules = {
      name: [(v: string) => !!v || 'Name is required'],
      number: [(v: number) => v >= 0 || 'Only positive numbers allowed'],
      categories: [
        (v: CategoryPreview[]) => (v && v.length > 0) || 'Category is required',
      ],
      shopifyProduct: [
        (v: ShopifyProduct) =>
          (v && v.id >= 1) || 'Product should be connected to real Shopify Product',
      ],
    };

    function setNewCategory(newCategory: (CategoryPreview | number)[]) {
      const newCategories: CategoryPreview[] = [];
      newCategory.forEach(cat => {
        if (typeof cat === 'number') {
          const old = state.entity.categories.find(item => item.id === cat);
          if (old) newCategories.push(old);
        } else {
          newCategories.push(cat);
        }
      });
      state.entity.categories = newCategories;
    }
    function setNewFontGroup(newFontGroup: FontGroup) {
      state.entity.fontGroup = newFontGroup;
    }

    function addLocation(newLocation: Location) {
      if (newLocation.imageFile && newLocation.name)
        state.entity.locations.push(newLocation);
    }

    function addAreaToLocation(colorizableArea: ColorizableArea) {
      const newColorizableArea = new ColorizableArea(colorizableArea);
      const locationIndex = state.entity.locations.findIndex(
        loc => loc.id === newColorizableArea.location?.id,
      );
      if (locationIndex > -1) {
        const colorizableAreaIndex = state.entity.locations[
          locationIndex
        ].imageFile.colorizableAreas?.findIndex(
          area => area.id === newColorizableArea.id,
        );
        if (colorizableAreaIndex !== undefined && colorizableAreaIndex > -1) {
          const { colorizableAreas } = state.entity.locations[locationIndex].imageFile;
          if (colorizableAreas)
            colorizableAreas[colorizableAreaIndex].colors = newColorizableArea.colors;
        }
      }
    }

    function changeShopifyProduct(newShopifyProduct: ShopifyProduct) {
      state.entity.shopifyProduct = new ShopifyProduct({
        id: newShopifyProduct.id,
        title: newShopifyProduct.title,
      });
      state.entity.sizes = newShopifyProduct.sizes;
    }

    function editLogoPlacement(val: any) {
      state.entity.logoPlacements = val;
    }

    function getHiddenAttributes() {
      const hidden = [];
      const option = state.entity.productCategoryOptions.find(
        item => item.keyWord === 'extraTop',
      );
      if (!option || !option.value) {
        hidden.push('extraTopLength');
      }
      return hidden;
    }

    return {
      state,
      title,
      rules,
      setNewCategory,
      setNewFontGroup,
      addLocation,
      categoryFilterDropdown,
      shopifyFilterDropdown,
      fontGroupFilterDropdown,
      DropdownFilter,
      addAreaToLocation,
      colorFilter,
      changeShopifyProduct,
      colorGroups,
      editLogoPlacement,
      getHiddenAttributes,
    };
  },
});
export default Component;
